import { useLocation, useNavigate } from "react-router-dom";
import { CalendarDays, User2, Heart, Search, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useSearchModal } from "@/contexts/SearchModalContext";

const navItems = [
  { id: 'search', label: "Explore", path: undefined, icon: Search, badge: undefined as number | undefined },
  { id: 'dashboard', label: "Dashboard", path: "/account?tab=dashboard", icon: LayoutDashboard, badge: undefined as number | undefined },
  { id: 'orders', label: "Trips", path: "/account?tab=orders", icon: CalendarDays, badge: 0 },
  { id: 'saved', label: "Saved", path: "/account?tab=saved", icon: Heart, badge: undefined as number | undefined },
  { id: 'profile', label: "Account", path: "/account?tab=profile", icon: User2, badge: undefined as number | undefined },
];

export const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { openSearch } = useSearchModal();
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;

    const handleResize = () => {
      const viewport = window.visualViewport;
      if (!viewport) return;
      
      const heightDiff = window.innerHeight - viewport.height;
      const keyboardOpen = heightDiff > 150;
      setKeyboardVisible(keyboardOpen);
    };

    window.visualViewport.addEventListener('resize', handleResize);
    window.visualViewport.addEventListener('scroll', handleResize);
    
    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
    };
  }, []);

  const isActive = (path?: string) => {
    if (!path) return false;
    const searchParams = new URLSearchParams(location.search);
    const currentTab = searchParams.get('tab');
    return path.includes(`tab=${currentTab}`) && location.pathname === '/account';
  };

  const handleNavClick = (item: typeof navItems[0], e: React.MouseEvent) => {
    if (item.id === 'search') {
      e.preventDefault();
      openSearch();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-[60] md:hidden pointer-events-none transition-all duration-300",
        keyboardVisible && "translate-y-full opacity-0"
      )}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <nav
        aria-label="Primary navigation"
        role="tablist"
        className="pointer-events-auto rounded-t-3xl border border-gold/20 border-b-0 bg-[rgba(10,10,10,.85)] backdrop-blur-xl shadow-[0_-8px_24px_rgba(0,0,0,.35)] overflow-hidden p-1.5"
      >
        {/* Navigation grid */}
        <div className="relative z-10 grid grid-cols-5 gap-0 h-[48px] items-center">
          {navItems.map((item, idx) => {
            const IconComponent = item.icon;
            const active = isActive(item.path);
            
            return (
              <button
                key={item.id}
                onClick={(e) => handleNavClick(item, e)}
                role="tab"
                aria-selected={active}
                aria-label={item.label}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "relative h-full flex flex-col items-center justify-center gap-1 rounded-[14px] select-none",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/30 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
                  "transition-all duration-300 hover:scale-105 active:scale-95",
                  active && "bg-[radial-gradient(80%_120%_at_50%_50%,rgba(212,175,55,.14),rgba(212,175,55,.06)_60%,transparent_100%)] border border-gold/30"
                )}
              >
                {/* Icon with scale animation */}
                <span className={cn(
                  "transition-all duration-300 ease-out",
                  active ? "scale-100" : "scale-[.96]"
                )}>
                  <IconComponent 
                    size={18}
                    className={cn(
                      "transition-colors duration-300",
                      active ? "text-ivory" : "text-ivory/60"
                    )}
                    strokeWidth={active ? 2.5 : 2}
                  />
                </span>
                
                {/* Label */}
                <span className={cn(
                  "text-[11px] font-medium leading-none whitespace-nowrap transition-all duration-300",
                  active ? "text-ivory" : "text-ivory/55"
                )}>
                  {item.label}
                </span>

                {/* Badge */}
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center bg-rose-500/90 text-white shadow-[0_2px_8px_rgba(244,63,94,.45)]">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};